import { Pipe, PipeTransform, Inject, LOCALE_ID } from "@angular/core";
import { locale } from "devextreme/localization";

@Pipe({ name: 'filedatetransform' })
export class FileDatePipe<TArgs, TReturn> implements PipeTransform {
  transform(value: any) {
    const date = new Date(value);
    return isNaN(date.getTime()) ? value : date;
  }
}

@Pipe({
  name: 'capitalizefirst'
})
export class CapitalizeFirstPipe<TArgs, TReturn> implements PipeTransform {
  transform(value: string): string {
    if (!value) return value;
    return value.charAt(0).toUpperCase() + value.slice(1);
  }
}

@Pipe({
  name: 'formatandcapitalize'
})
export class FormatAndCapitalizePipe implements PipeTransform {
  transform(value: string): string {
    if (!value) return value;
    return value
      .replace(/([A-Z])/g, ' $1')
      .trim()  // Remove leading spaces
      .replace(/^./, char => char.toUpperCase());
  }

}

@Pipe({
  name: 'formatbooleannumbersdates'
})
export class FormatBooleanNumersDatesPipe implements PipeTransform {
  constructor(@Inject(LOCALE_ID) private locale: string) {}

  transform(value: any): any {
    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }
    else if (typeof value === 'number') {
      return value.toLocaleString(this.locale); // Use the application's locale
    }
    else if (typeof value === 'string' && this.isValidDateString(value)) {
      const date = new Date(value);
      return date.toLocaleDateString(this.locale); // Use the application's locale
    }
    return value;
  }

  private isValidDateString(value: string): boolean {
    // Check if the string looks like a date format
    const datePatterns = [
      /^\d{4}-\d{2}-\d{2}$/, // YYYY-MM-DD
      /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/, // ISO datetime
      /^\d{1,2}\/\d{1,2}\/\d{4}$/, // MM/DD/YYYY or M/D/YYYY
      /^\d{1,2}-\d{1,2}-\d{4}$/, // MM-DD-YYYY or M-D-YYYY
      /^\d{4}\/\d{1,2}\/\d{1,2}$/, // YYYY/MM/DD or YYYY/M/D
    ];

    // First check if it matches common date patterns
    const matchesPattern = datePatterns.some(pattern => pattern.test(value.trim()));

    if (!matchesPattern) {
      return false;
    }

    // Then verify it's actually a valid date
    const date = new Date(value);
    return !isNaN(date.getTime());
  }
}

@Pipe({
  name: 'formattaskdata'
})
export class FormatTaskDataPipe implements PipeTransform {
  transform(value: any, existingColumns?: string[] | string, ...args: unknown[]): string {
    if (value === null || value === undefined) {
      return '';
    }

    // The input is always a JSON string
    if (typeof value === 'string') {
      try {
        const parsedData = JSON.parse(value);

        // Get existing column names for filtering
        let columnNames: string[] = [];
        if (existingColumns) {
          if (typeof existingColumns === 'string') {
            // If a single column name is passed
            columnNames = [existingColumns];
          } else if (Array.isArray(existingColumns)) {
            // If an array of column names is passed
            columnNames = existingColumns;
          }
        }

        // Normalize column names for comparison (convert to lowercase for case-insensitive comparison)
        const normalizedColumnNames = columnNames.map(col => col.toLowerCase());

        // Check if we have both currency and amount for special formatting
        const hasCurrency = 'currency' in parsedData;
        const hasAmount = 'amount' in parsedData;
        const currencyCode = hasCurrency ? parsedData.currency : null;

        // Filter out entries that match existing column names or are explicitly excluded
        const entries = Object.entries(parsedData).filter(([key, val]) => {
          // Exclude the 'description' key specifically
          if (key.toLowerCase() === 'description') {
            // console.log(`Filtering out key: ${key} (description field excluded)`);
            return false;
          }

          // Exclude responsiblePersonId and responsibleReviewerId
          if (key === 'responsiblePersonId' || key === 'responsibleReviewerId') {
            return false;
          }


          // Convert camelCase key to various formats for comparison
          const keyLower = key.toLowerCase();
          const keyTitleCase = this.formatKey(key).toLowerCase();
          const keySpaced = key.replace(/([A-Z])/g, ' $1').trim().toLowerCase();

          // Debug: Log the key variations and column names for troubleshooting
          // console.log('Key variations:', { original: key, keyLower, keyTitleCase, keySpaced });
          // console.log('Column names to check against:', normalizedColumnNames);

          // Check if any of these variations match existing column names
          const isMatch = normalizedColumnNames.some(colName =>
            colName === keyLower ||
            colName === keyTitleCase ||
            colName === keySpaced ||
            colName.replace(/\s+/g, '') === keyLower ||
            colName.replace(/\s+/g, '').toLowerCase() === key.toLowerCase() ||
            this.isFieldMappedToColumn(key, colName)
          );

          // Debug: Log if this key is being filtered out
          if (isMatch) {
            // console.log(`Filtering out key: ${key} (matched column)`);
          }

          return !isMatch;
        });

        // If no entries remain after filtering, return empty string
        if (entries.length === 0) {
          return '';
        }

        const formattedEntries = entries.map(([key, val]) => {
          // Format the key to be more readable (convert camelCase to Title Case)
          const formattedKey = this.formatKey(key);

          // Special handling for amount when currency is present
          let formattedValue: string;
          if (key === 'amount' && hasCurrency && currencyCode && typeof val === 'number') {
            formattedValue = this.formatCurrencyAmount(val, currencyCode);
          } else {
            formattedValue = this.formatValue(val, parsedData, key);
          }

          const keyValuePair = `<strong>${formattedKey}</strong>: ${formattedValue}`;

          // Check if this key-value pair is longer than 160 characters
          if (keyValuePair.length > 160) {
            // For very long values, truncate with tooltip
            const truncatedValue = formattedValue.length > 100 ?
              formattedValue.substring(0, 100) + '...' : formattedValue;
            return `<strong>${formattedKey}</strong>: <span title="${formattedValue.replace(/"/g, '&quot;')}">${truncatedValue}</span>`;
          }

          return keyValuePair;
        });

        return this.wrapText(formattedEntries.join(', '), 160);
      } catch (error) {
        // If JSON parsing fails, return the original string
        return String(value);
      }
    }

    // Fallback for non-string inputs
    return String(value);
  }

  private formatKey(key: string): string {
    // Convert camelCase to Title Case
    return key
      .replace(/([A-Z])/g, ' $1') // Insert space before uppercase letters
      .replace(/^./, char => char.toUpperCase()) // Capitalize first letter
      .trim();
  }

  private formatValue(value: any, parsedData?: any, currentKey?: string): string {
    if (value === null || value === undefined) {
      return '';
    }

    // Handle booleans
    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }

    // Handle numbers (but not amount when currency is present)
    if (typeof value === 'number') {
      return value.toLocaleString();
    }

    // Handle arrays
    if (Array.isArray(value)) {
      return this.formatArrayValue(value, currentKey);
    }

    // Handle objects (nested)
    if (typeof value === 'object') {
      try {
        return JSON.stringify(value);
      } catch (error) {
        return String(value);
      }
    }

    // Handle strings
    if (typeof value === 'string') {
      // Check if it looks like a date first
      const dateValue = new Date(value);
      if (!isNaN(dateValue.getTime()) && (value.includes('-') || value.includes('/')) && value.length >= 8) {
        return dateValue.toLocaleDateString();
      }

      // Check if the string is in camelCase and convert to Sentence Case
      if (this.isCamelCase(value)) {
        return this.formatCamelCaseToSentenceCase(value);
      }
    }

    return String(value);
  }

  private formatCurrencyAmount(amount: number, currencyCode: string): string {
    try {
      // Use Intl.NumberFormat for proper currency formatting with accounting style
      const formatter = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currencyCode.toUpperCase(),
        currencyDisplay: 'symbol',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });

      return formatter.format(amount);
    } catch (error) {
      // Fallback if currency code is not recognized
      const symbol = this.getCurrencySymbol(currencyCode);
      return `${symbol}${amount.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })}`;
    }
  }

  private getCurrencySymbol(currencyCode: string): string {
    const symbols: { [key: string]: string } = {
      'USD': '$',
      'EUR': '€',
      'GBP': '£',
      'JPY': '¥',
      'CAD': 'C$',
      'AUD': 'A$',
      'CHF': 'CHF',
      'CNY': '¥',
      'INR': '₹',
      'KRW': '₩',
      'MXN': '$',
      'BRL': 'R$',
      'RUB': '₽',
      'ZAR': 'R',
      'SEK': 'kr',
      'NOK': 'kr',
      'DKK': 'kr',
      'PLN': 'zł',
      'CZK': 'Kč',
      'HUF': 'Ft',
      'TRY': '₺',
      'ILS': '₪',
      'AED': 'د.إ',
      'SAR': '﷼',
      'THB': '฿',
      'SGD': 'S$',
      'HKD': 'HK$',
      'NZD': 'NZ$'
    };

    return symbols[currencyCode.toUpperCase()] || currencyCode.toUpperCase() + ' ';
  }

  private wrapText(text: string, maxWidth: number): string {
    if (text.length <= maxWidth) {
      return text;
    }

    // Split by key-value pairs (separated by ', ')
    const pairs = text.split(', ');
    const lines: string[] = [];
    let currentLine = '';

    for (let i = 0; i < pairs.length; i++) {
      const pair = pairs[i];
      const isLastPair = i === pairs.length - 1;
      const separator = isLastPair ? '' : ', ';
      const testLine = currentLine ? `${currentLine}, ${pair}` : pair;

      // Check if adding this pair would exceed the width
      if (testLine.length > maxWidth && currentLine) {
        // Start a new line with the current pair
        lines.push(currentLine);
        currentLine = pair;
      } else {
        // Add the pair to the current line
        currentLine = testLine;
      }
    }

    // Add the last line if it has content
    if (currentLine) {
      lines.push(currentLine);
    }

    // Join lines with HTML line breaks
    return lines.join('<br>');
  }

  /**
   * Check if a JSON field should be mapped to a specific column name
   * Handle cases where field names don't directly match column names
   */
  private isFieldMappedToColumn(fieldKey: string, columnName: string): boolean {
    const fieldLower = fieldKey.toLowerCase();
    const columnLower = columnName.toLowerCase();

    // Define field-to-column mappings
    const fieldMappings: { [key: string]: string[] } = {
      'loannumber': ['reference name', 'reference', 'loan reference', 'loan ref'],
      'requesttype': ['request type', 'type', 'request kind'],
      'amount': ['amount', 'value', 'sum', 'total'],
      'currency': ['currency', 'curr', 'currency code'],
      'description': ['description', 'desc', 'notes', 'comments'],
      'status': ['status', 'state', 'condition'],
      'priority': ['priority', 'urgency', 'importance'],
      'assignee': ['assignee', 'assigned to', 'owner'],
      'duedate': ['due date', 'deadline', 'target date'],
      'createdate': ['create date', 'created', 'date created'],
      'updatedate': ['update date', 'updated', 'date updated', 'modified'],
      'id': ['id', 'identifier', 'key', 'reference id'],
      'name': ['name', 'title', 'subject'],
      'borrower': ['borrower', 'client', 'customer'],
      'property': ['property', 'asset', 'collateral'],
      'servicer': ['servicer', 'service provider'],
      'lender': ['lender', 'bank', 'institution']
    };

    // Check if the field has any mapped column names
    if (fieldMappings[fieldLower]) {
      return fieldMappings[fieldLower].some(mappedName =>
        columnLower === mappedName ||
        columnLower.includes(mappedName) ||
        mappedName.includes(columnLower)
      );
    }

    return false;
  }

  /**
   * Format array values to show keys and values in a sub-list with round brackets
   */
  private formatArrayValue(array: any[], arrayKey?: string): string {
    if (array.length === 0) {
      return '()';
    }

    // Check if this is a Loan Amounts array
    const isLoanAmountsArray = arrayKey &&
      (arrayKey.toLowerCase() === 'loan amounts' || arrayKey.toLowerCase() === 'loanamounts');

    const formattedItems = array.map((item, index) => {
      if (item === null || item === undefined) {
        return isLoanAmountsArray ? '(empty)' : `${index}: (empty)`;
      }

      // If item is an object, format its key-value pairs
      if (typeof item === 'object' && !Array.isArray(item)) {
        const entries = Object.entries(item);
        if (entries.length === 0) {
          return isLoanAmountsArray ? '(empty object)' : `${index}: (empty object)`;
        }

        if (isLoanAmountsArray) {
          // Special formatting for Loan Amounts - only show Amount and Loan Number
          const formattedEntries = entries
            .filter(([key]) => {
              const keyLower = key.toLowerCase();
              return keyLower === 'amount' || keyLower === 'loannumber' || keyLower === 'loan number';
            })
            .map(([key, val]) => {
              const formattedKey = this.formatKey(key);
              let formattedValue: string;

              // Special handling for amount values in loan amounts array
              if (key.toLowerCase() === 'amount' && typeof val === 'number') {
                // Use USD as default currency if no currency is specified
                formattedValue = this.formatCurrencyAmount(val, 'USD');
              } else {
                formattedValue = this.formatValue(val, undefined);
              }

              return `<strong>${formattedKey}</strong>: ${formattedValue}`;
            });

          return formattedEntries.join(', ');
        } else {
          // Regular object formatting with index
          const formattedEntries = entries.map(([key, val]) => {
            const formattedKey = this.formatKey(key);
            const formattedValue = this.formatValue(val, undefined);
            return `${formattedKey}: ${formattedValue}`;
          });

          return `${index}: {${formattedEntries.join(', ')}}`;
        }
      }

      // For primitive values, format them directly
      const formattedValue = this.formatValue(item, undefined);
      return isLoanAmountsArray ? formattedValue : `${index}: ${formattedValue}`;
    });

    return `(${formattedItems.join(', ')})`;
  }

  /**
   * Check if a string is in camelCase format
   */
  private isCamelCase(value: string): boolean {
    // Must contain at least one lowercase letter followed by an uppercase letter
    // Should not contain spaces, hyphens, underscores, or start with uppercase
    return /^[a-z][a-zA-Z0-9]*[A-Z][a-zA-Z0-9]*$/.test(value) &&
           !value.includes(' ') &&
           !value.includes('-') &&
           !value.includes('_');
  }

  /**
   * Convert camelCase string to Sentence Case
   * e.g., "firstName" becomes "First name"
   */
  private formatCamelCaseToSentenceCase(value: string): string {
    return value
      .replace(/([A-Z])/g, ' $1') // Insert space before uppercase letters
      .replace(/^./, char => char.toUpperCase()) // Capitalize first letter
      .trim();
  }
}

@Pipe({
  name: 'allcapstosentencecase'
})
export class AllCapsToSentenceCasePipe implements PipeTransform {
  transform(value: string): string {
    if (!value || typeof value !== 'string') {
      return value;
    }

    // Check if the string is all uppercase (with possible spaces or underscores)
    if (value === value.toUpperCase()) {
      return value
        .toLowerCase() // Convert to lowercase first
        .replace(/^./, char => char.toUpperCase()) // Capitalize first letter
        .replace(/\s+/g, ' ') // Normalize spaces
        .trim();
    }

    return value;
  }
}

@Pipe({
  name: 'flexcurrency'
})
export class FlexCurrencyPipe implements PipeTransform {
  transform(value: any, currency?: string): string {
    if (!value && value !== 0) {
      return '';
    }

    const amount = parseFloat(value);
    if (isNaN(amount)) {
      return value?.toString() || '';
    }

    // Use provided currency or default to USD
    const currencyCode = currency || 'USD';

    // Use Intl.NumberFormat for proper currency formatting with accountancy style
    try {
      const formatter = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currencyCode,
        currencySign: 'accounting', // Use accounting format (parentheses for negative numbers)
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });

      return formatter.format(amount);
    } catch (error) {
      // Fallback if currency code is invalid
      console.warn(`Invalid currency code: ${currencyCode}, using USD as fallback`);
      const fallbackFormatter = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        currencySign: 'accounting',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });

      return fallbackFormatter.format(amount);
    }
  }
}